import React, { useId } from "react";
import { Radio, Callout, HelpText } from "../ui";
import { useContentProtection } from "../../contexts/ContentProtectionContext";
import {
  ACCESS_COPY, ACCESS_LEVELS, accessFloor, isLooserThan, isViewableWithoutDownload,
} from "./contentAccess";

/**
 * StudentAccessPicker — "what may a student do with this file?", on every form
 * that uploads or edits a file students open.
 *
 * Controlled: `value` is 'download' | 'view' | 'view-no-copy'. The page decides
 * the starting value (defaultAccessFor) and saves it; this only renders the
 * choice and the two things an author must know before choosing:
 *
 *   • the institute may ENFORCE a minimum, so looser options are disabled and
 *     it says where that is set;
 *   • a view-only file of a type the browser cannot render (a ZIP) can then
 *     not be opened by students at all.
 *
 * Staff are never restricted — this is only ever about students.
 */
export default function StudentAccessPicker({ value, onChange, fileType, isVideo = false, disabled = false }) {
  const { policy } = useContentProtection();
  const name = useId();
  const floor = accessFloor(policy);

  if (isVideo) {
    return (
      <HelpText>
        Students stream videos and can never download them. Videos are always watermarked with the student's name.
      </HelpText>
    );
  }

  const unviewable = value && value !== "download" && fileType && !isViewableWithoutDownload(fileType);

  return (
    <fieldset className="tw-m-0 tw-flex tw-min-w-0 tw-flex-col tw-gap-2 tw-border-0 tw-p-0">
      <legend className="tw-mb-1 tw-p-0 tw-text-sm tw-font-semibold tw-text-content">Student access</legend>
      {ACCESS_LEVELS.map((level) => (
        <Radio
          key={level}
          name={name}
          value={level}
          checked={value === level}
          onChange={() => onChange(level)}
          disabled={disabled || isLooserThan(level, floor)}
          label={ACCESS_COPY[level].label}
          description={ACCESS_COPY[level].description}
        />
      ))}
      {floor !== "download" && (
        <Callout tone="info">
          Your institute requires at least "{ACCESS_COPY[floor].label}" for every file. Change it in Settings → Content Protection.
        </Callout>
      )}
      {unviewable && (
        <Callout tone="warning">
          Students can't open a {String(fileType).toUpperCase()} file without downloading it, so a view-only copy is
          unreadable to them. Upload a PDF instead, or allow downloads.
        </Callout>
      )}
    </fieldset>
  );
}
